import React, { useContext, useEffect, useMemo, useState } from 'react';
import { isEmpty } from 'lodash';
import { DocumentsContext } from 'context/DocumentsContext';
import { InformationDashboard409AContext } from 'context/InformationDashboard409AContext';
import {
  SEARCH_DELAY
} from 'dashboard409a/pages/Information/CurrentRequestedDocumentsAndQuestions/CurrentREquestedDocumentsAndQuestions.constants';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import { RequestedFile } from 'pages/RequestDocuments/components/FolderIcon/types';
import DocumentToolbar from './DocumentToolbar';
import PendingDocumentTable from './PendingDocumentTable';
import { CurrentRequestedDocumentTablesProps } from './types';
import UploadedDocumentsTable from './UploadedDocumentsTable';
import { filterDelegatableQuestions } from '../util/filterInformationRequests';

const CurrentRequestedDocumentTables = ({ documents, questions }: CurrentRequestedDocumentTablesProps) => {
  const { handleGetCurrentInformationRequests, currentDocuments, refreshDocuments, permissions } = useContext(
    InformationDashboard409AContext
  );
  const [uploadedFiles, setUploadedFiles] = useState<RequestedFile[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebouncedValue(searchQuery, SEARCH_DELAY);
  const unmetRequests = useMemo(
    () =>
      documents?.progress.filter(request => {
        const isNa =
          request.fulfillment_status === 'not_applicable'
          || request.fulfillment_status === 'not_available'
          || request.is_fulfilled;
        return (request.files?.length ?? 0) === 0 && !isNa;
      }),
    [documents]
  );
  const unansweredQuestions = useMemo(() => filterDelegatableQuestions(questions || []), [questions]);
  const documentsContextValue = useMemo(() => ({ currentDocuments }), [currentDocuments]);

  // Show pending section if there are unmet document requests OR unanswered questions
  const hasPendingItems = !isEmpty(unmetRequests) || !isEmpty(unansweredQuestions);

  useEffect(() => {
    refreshDocuments();
    handleGetCurrentInformationRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadedFiles]);

  return (
    <DocumentsContext.Provider value={documentsContextValue}>
      {hasPendingItems && permissions?.canUseInfoRequestLedger && (
        <PendingDocumentTable documents={documents} unmetRequests={unmetRequests} setUploadedFiles={setUploadedFiles} questions={questions} />
      )}
      {!isEmpty(currentDocuments) && (
        <DocumentToolbar
          currentDocuments={{ ...currentDocuments, files: currentDocuments.files ?? [] }}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
        />
      )}
      <UploadedDocumentsTable documents={documents} searchQuery={debouncedSearchQuery} />
    </DocumentsContext.Provider>
  );
};

export default CurrentRequestedDocumentTables;
